import React, { useState, useEffect } from 'react';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';

export default function AdminConfigSmtp() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [success, setSuccess] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [testErr, setTestErr] = useState('');
  const [testHint, setTestHint] = useState('');
  const [form, setForm] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    from: 'Auction <noreply@example.com>',
    appUrl: 'https://app.auction.com.br',
  });

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const data = await apiAdmin.get('/api/admin/config/smtp');
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        host: data.host || '',
        port: data.port ?? 587,
        secure: data.secure ?? false,
        user: data.userConfigured ? '********' : (data.user || ''),
        pass: '',
        from: data.from || prev.from,
        appUrl: data.appUrl || prev.appUrl,
      }));
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao carregar configuração. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? e.target.checked : value,
    }));
    setErro('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErro('');
    setSuccess('');
    try {
      const payload = {
        host: form.host?.trim() || undefined,
        port: form.port ? Number(form.port) : 587,
        secure: !!form.secure,
        from: form.from?.trim() || undefined,
        appUrl: form.appUrl?.trim() || undefined,
      };
      if (form.user && form.user !== '********') payload.user = form.user.trim();
      if (form.pass) payload.pass = form.pass;
      const data = await apiAdmin.put('/api/admin/config/smtp', payload);
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        host: data.host || '',
        port: data.port ?? 587,
        secure: data.secure ?? false,
        user: data.userConfigured ? '********' : '',
        pass: '',
        from: data.from || prev.from,
        appUrl: data.appUrl || prev.appUrl,
      }));
      setSuccess('Configuração SMTP salva com sucesso.');
    } catch (err) {
      setErro(getDisplayMessage(err, 'Erro ao salvar configuração. Tente novamente.'));
    } finally {
      setSaving(false);
    }
  };

  const enviarTesteSmtp = async () => {
    const to = testEmail?.trim();
    if (!to || !to.includes('@')) {
      setTestErr('Informe um e-mail válido para receber o teste.');
      setTestMsg('');
      return;
    }
    setTestLoading(true);
    setTestErr('');
    setTestMsg('');
    setTestHint('');
    try {
      const data = await apiAdmin.post('/api/admin/config/smtp/test', { to });
      if (data?.ok) {
        setTestMsg(data.message || 'E-mail de teste enviado. Verifique a caixa de entrada e o spam.');
      } else {
        setTestErr(data?.error || 'Falha no teste.');
        if (typeof data?.hint === 'string') setTestHint(data.hint);
      }
    } catch (err) {
      const body = err?.data;
      const msg =
        body && typeof body.error === 'string'
          ? [body.error, body.smtpResponse, body.code].filter(Boolean).join(' — ')
          : getDisplayMessage(err, 'Falha ao enviar teste.');
      setTestErr(msg);
      if (body && typeof body.hint === 'string') setTestHint(body.hint);
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">Configuração SMTP (E-mail)</h1>
      <p className="text-sm text-slate-600 mb-4">
        Configure o servidor SMTP para envio de e-mails (dados de acesso, redefinição de senha, etc.).
      </p>

      {erro && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {erro}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Servidor SMTP (Host)</label>
          <input
            type="text"
            name="host"
            value={form.host}
            onChange={handleChange}
            placeholder="smtp.gmail.com"
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
          />
          <p className="mt-1 text-xs text-slate-500">
            Gmail: smtp.gmail.com | Outlook: smtp.office365.com | SendGrid: smtp.sendgrid.net
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Porta</label>
            <input
              type="number"
              name="port"
              value={form.port}
              onChange={handleChange}
              placeholder="587"
              min="1"
              max="65535"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
            <p className="mt-1 text-xs text-slate-500">Geralmente 587 (TLS) ou 465 (SSL)</p>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                name="secure"
                checked={form.secure}
                onChange={handleChange}
                className="rounded"
              />
              Conexão SSL/TLS (porta 465)
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Usuário (E-mail)</label>
          <input
            type="text"
            name="user"
            value={form.user}
            onChange={handleChange}
            placeholder={config?.userConfigured ? 'Deixe em branco para manter' : 'seu-email@gmail.com'}
            autoComplete="username"
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
          />
          {config?.userConfigured && (
            <p className="mt-1 text-xs text-slate-500">Usuário já configurado. Informe um novo apenas para alterar.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Senha</label>
          <input
            type="password"
            name="pass"
            value={form.pass}
            onChange={handleChange}
            placeholder={config?.passConfigured ? 'Deixe em branco para manter' : 'Senha ou Senha de App'}
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
          />
          {config?.passConfigured && (
            <p className="mt-1 text-xs text-slate-500">Senha já configurada. Informe uma nova apenas para alterar.</p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            Gmail / Google Workspace: ative verificação em 2 etapas e use <strong>Senha de app</strong> (não a senha da
            conta). O <strong>Usuário</strong> deve ser o mesmo e-mail que autentica no SMTP (ex.: staff@seu-dominio).
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Remetente (De)</label>
          <input
            type="text"
            name="from"
            value={form.from}
            onChange={handleChange}
            placeholder="Auction <noreply@example.com>"
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
          />
          <p className="mt-1 text-xs text-slate-500">
            Com <strong>smtp.gmail.com</strong>, o endereço entre &lt;&gt; deve ser o mesmo do <strong>Usuário</strong> (ou
            um alias autorizado em &quot;Enviar e-mail como&quot; no Google). Caso contrário, o envio pode falhar — o
            sistema ajusta o remetente e usa Reply-To quando necessário.
          </p>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-2">
          <label className="block text-sm font-medium text-slate-700">Testar envio (diagnóstico)</label>
          <p className="text-xs text-slate-600">
            Salve a configuração antes. O servidor executa <code className="text-[11px]">verify()</code> e envia um
            e-mail simples; em caso de erro, a resposta mostra a mensagem do provedor (ex.: credencial inválida).
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => {
                setTestEmail(e.target.value);
                setTestErr('');
                setTestMsg('');
                setTestHint('');
              }}
              placeholder="e-mail que receberá o teste"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
            <button
              type="button"
              disabled={testLoading}
              onClick={() => void enviarTesteSmtp()}
              className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-500 disabled:opacity-50 text-sm shrink-0"
            >
              {testLoading ? 'Enviando…' : 'Enviar e-mail de teste'}
            </button>
          </div>
          {testMsg && <p className="text-sm text-green-700">{testMsg}</p>}
          {testErr && (
            <p className="text-sm text-red-700 whitespace-pre-wrap break-words" role="alert">
              {testErr}
            </p>
          )}
          {testHint && (
            <div
              className="text-sm text-slate-800 bg-amber-50 border border-amber-200 rounded-md p-3 whitespace-pre-wrap break-words"
              role="note"
            >
              {testHint}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">URL da Aplicação (APP_URL)</label>
          <input
            type="url"
            name="appUrl"
            value={form.appUrl}
            onChange={handleChange}
            placeholder="https://app.auction.com.br"
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
          />
          <p className="mt-1 text-xs text-slate-500">Usada nos links de redefinição de senha e dados de acesso.</p>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 disabled:opacity-50 text-sm"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
